/**
 * ============================================================================
 * 埋点日志服务客户端 SDK (StatService)
 * ============================================================================
 *
 * 一、 核心架构特性：
 * 1. 极致非阻塞（Zero-Await）：
 *    - track() 方法为纯同步函数，业务调用瞬间压入内存队列立即返回，绝不占用主线程微秒级以上的时间，杜绝卡顿。
 * 2. 8大元数据纯同步提取：
 *    - timestamp、platform、app_key、app_path、app_event、user_id、ip、extra 全部同步获取，
 *      平台型号与登录状态在后台静默预热并实时同步至内存变量中。
 * 3. 内存优先 + 存储防抖（I/O 削峰）：
 *    - 不会在每次打点时频繁读写底层存储，所有持久化操作在后台异步防抖合并，保护磁盘寿命与性能。
 * 4. 原生环境与网络逃生闭环：
 *    - 深度结合 Capacitor 的 appStateChange 原生事件，App 退后台、被杀前夕使用 Beacon/KeepAlive 强制清仓。
 * 5. 纯文本流协议对齐：
 *    - 格式：timestamp|platform|app_key|app_path|app_event|user_id|ip|extra，换行符分隔。
 *
 * 二、 快速接入指引：
 *
 * 1. 入口注册 (src/main.js):
 *    import { createApp } from 'vue';
 *    import App from './App.vue';
 *    import router from './router';
 *    import { StatService } from '@/mechanism/statService';
 *
 *    const app = createApp(App);
 *    app.use(router);
 *
 *    // 初始化并挂载埋点引擎
 *    app.use(StatService, {
 *        router,
 *        reportUrl: 'https://your-api-domain.com/stat/report',
 *        batchSize: 10,        // 达到 10 条立即触发批量发送
 *        flushInterval: 3000   // 超过 3000ms 未满额也强制发送
 *    });
 *
 *    app.mount('#app');
 *
 * 2. 业务使用 (仅需传递 event 和 extra，支持任何上下文):
 *    // 方式 A: 任意 js/ts/vue 文件中直接导入调用 (推荐)
 *    import { trackStat } from '@/mechanism/statService';
 *    trackStat('button_click', { button_name: '立即下单' });
 *
 *    // 方式 B: Vue 模板/组件实例中直接调用
 *    this.$stat('banner_view', { banner_id: 102 });
 * ============================================================================
 */

import { App as CapacitorApp } from '@capacitor/app';
import { isHarmony, isIOS, isAndroid, isWxMiniProgram } from './nativeBridge';
import { Storage } from './storage';
import { userStore } from '@/store/userStore';
import { URL_CONFIG } from "@/url_config";


// 本地持久化存储的 Key
const STAT_PERSIST_KEY = 'stat_event_buffer_queue';

class StatManager {
    constructor() {
        this.router = null;
        this.reportUrl = `${URL_CONFIG.agent_server_url}/api/v1/stat/report`;
        this.batchSize = 10;           // 容量阈值
        this.flushInterval = 3000;      // 时间阈值（毫秒）

        // 内存双队列设计
        this.queue = [];               // 待发送主队列 (内存态)
        this.inFlightBatch = [];       // 正在网络链路中传输的队列 (防止重入丢失)

        this.isFlushing = false;        // 单向排它锁
        this.timer = null;             // 时间轮询心跳定时器
        this.initialized = false;

        // 内存快速镜像变量（纯同步访问）
        this._platform = 'unknown';
        this._storageSyncTimer = null; // 本地持久化写入防抖器
        this._platformLocked = false;      // 标记精准厂商是否已获取并锁定
        this._isFetchingPlatform = false;   // 防重复并发拉取锁
    }

    /**
     * 引擎初始化入口
     * @param {Object} options 配置项
     */
    init(options = {}) {
        if (this.initialized) return;
        this.initialized = true;

        if (options.router) this.router = options.router;
        if (options.reportUrl) this.reportUrl = options.reportUrl;
        if (options.batchSize) this.batchSize = options.batchSize;
        if (options.flushInterval) this.flushInterval = options.flushInterval;

        // 1. 同步预置基础平台标识
        this._presetPlatformFast();

        // 2. 启动后台异步任务：精确拉取原生设备型号（如 vivo/xiaomi 等）
        this._prefetchAccuratePlatform();

        // 3. 启动后台异步任务：恢复上次未发完的离线数据
        this._recoverOfflineQueue();

        // 4. 挂载原生容器与 Web 临终事件监听
        this._bindLifecycleListeners();

        // 5. 启动轮询心跳
        this._startTicker();
    }

    // =========================================================================
    // 纯同步元数据提取器（耗时 < 0.01ms，绝对不阻塞）
    // =========================================================================

    /**
     * 字段 1: 获取整型秒级时间戳 (纯同步)
     */
    getTimestamp() {
        return Math.floor(Date.now() / 1000);
    }

    /**
     * 字段 2: 获取运行平台 (纯同步读取内存缓存)
     */
    getPlatform() {
        // 如果是泛安卓且尚未锁定具体的厂商型号，尝试在后台静默提精
        if (this._platform === 'android' && !this._platformLocked) {
            this._prefetchAccuratePlatform();
        }
        return this._platform;
    }


    /**
     * 字段 3: 获取应用 AppKey (纯同步)
     */
    getAppKey() {
        return process.env.VUE_APP_APP_ID || 'tally';
    }

    /**
     * 字段 4: 自动提取路由 (纯同步)
     */
    getCurrentPath() {
        try {
            if (this.router && this.router.currentRoute && this.router.currentRoute.value) {
                return this.router.currentRoute.value.path || '/';
            }
            if (typeof window !== 'undefined' && window.location) {
                return window.location.pathname || window.location.hash || '/';
            }
        } catch (e) {
            // 兜底防爆
        }
        return '/';
    }

    /**
     * 字段 6: 获取当前登录用户的 user_id (纯同步读取 store 响应式变量)
     */
    getUserId() {
        try {
            if (userStore && userStore.userInfo) {
                const uid = userStore.userInfo.id || userStore.userInfo.user_id || userStore.userInfo.uid;
                if (uid !== undefined && uid !== null) {
                    return String(uid);
                }
            }
        } catch (e) {
            // 兜底防爆
        }
        return '';
    }

    /**
     * 字段 7: 客户端 IP (纯同步)
     * 前端留空，服务端按协议会自动提取前置代理真实出口 IP
     */
    getClientIp() {
        return '';
    }

    // =========================================================================
    // 内部平台预判与异步提精
    // =========================================================================

    /**
     * 同步预判基础环境，确保在初始化第 0 毫秒就有合理的 platform
     */
    _presetPlatformFast() {
        try {
            if (isWxMiniProgram()) {
                this._platform = 'mini_program';
            } else if (isHarmony()) {
                this._platform = 'harmony';
            } else if (isIOS()) {
                this._platform = 'ios';
            } else if (isAndroid()) {
                this._platform = 'android';
            } else {
                this._platform = 'web';
            }
        } catch (e) {
            this._platform = 'web';
        }
    }

    /**
     * 后台静默精细化检测（如安卓读取设备型号）
     */
    _prefetchAccuratePlatform() {
        // 已经锁定或者当前正在异步读取中，直接跳过
        if (this._platformLocked || this._isFetchingPlatform) return;

        this._isFetchingPlatform = true;

        setTimeout(async () => {
            try {
                if (isAndroid()) {
                    // 读取由 PushManager 写入的设备信息
                    const pushData = await Storage.get('device_push_data');
                    if (pushData && pushData.platform && pushData.platform.toLowerCase() !== 'android') {
                        // 1. 更新为精确厂商（如 xiaomi, vivo, oppo 等）
                        this._platform = String(pushData.platform).toLowerCase();
                        // 2. 彻底锁住，后续 getPlatform() 不再重复进入读取流程
                        this._platformLocked = true;
                    }
                }
            } catch (err) {
                // 静默失败，保持现状
            } finally {
                this._isFetchingPlatform = false;
            }
        }, 0);
    }

    // =========================================================================
    // 业务打点主逻辑 (纯同步，零阻碍)
    // =========================================================================

    /**
     * 业务调用入口：仅此一个！
     * @param {string} appEvent 事件名称 (必填)
     * @param {Object|string} extra 自定义扩展字段 (选填)
     */
    track(appEvent, extra = {}) {
        if (!appEvent) return;

        // 1. 规范化 extra 字段 (防换行符破坏协议规范)
        let extraStr = '{}';
        if (typeof extra === 'object' && extra !== null) {
            try {
                extraStr = JSON.stringify(extra);
            } catch (e) {
                extraStr = '{}';
            }
        } else if (typeof extra === 'string') {
            extraStr = extra;
        }
        extraStr = extraStr.replace(/[\r\n]/g, '');

        // 2. 纯同步拼接 8 字段 (以 | 分割)
        // 结构: timestamp|platform|app_key|app_path|app_event|user_id|ip|extra
        const itemLine = [
            this.getTimestamp(),
            this.getPlatform(),
            this.getAppKey(),
            this.getCurrentPath(),
            String(appEvent).trim(),
            this.getUserId(),
            this.getClientIp(),
            extraStr
        ].join('|');

        // 3. 瞬间压入内存主队列 (纳秒级操作)
        this.queue.push(itemLine);

        // 4. 后台防抖落盘，不阻塞主线程
        this._scheduleStoragePersist();

        // 5. 容量阈值达到，唤醒后台非阻塞上报
        if (this.queue.length >= this.batchSize) {
            this._triggerFlush(false);
        }
    }

    // =========================================================================
    // 异步调度与网络流水线 (独立运行在主业务之后)
    // =========================================================================

    /**
     * 启动心跳定时器
     */
    _startTicker() {
        if (this.timer) clearInterval(this.timer);
        this.timer = setInterval(() => {
            if (this.queue.length > 0) {
                this._triggerFlush(false);
            }
        }, this.flushInterval);
    }

    /**
     * 触发异步冲刷任务（非阻塞函数，外部调用直接返回）
     * @param {boolean} isEmergency 是否为切后台/关闭时的紧急通道
     */
    _triggerFlush(isEmergency = false) {
        // 放进下一事件循环微任务执行，让出主线程
        Promise.resolve().then(async () => {
            if (this.queue.length === 0) return;
            // 状态锁：如果有普通任务在运行中且非紧急任务，静默跳过
            if (this.isFlushing && !isEmergency) return;

            this.isFlushing = true;

            // 1. 将数据从主队列转移到正在发送中队列 (In-Flight)，防止发送失败时丢失新来的数据
            const currentBatch = [...this.queue];
            this.queue = [];
            this.inFlightBatch = currentBatch;

            const payloadText = currentBatch.join('\n');

            // 2. 紧急通道处理 (App 挂起、退后台、锁屏)
            if (isEmergency && typeof navigator !== 'undefined' && navigator.sendBeacon) {
                try {
                    const blob = new Blob([payloadText], { type: 'text/plain; charset=utf-8' });
                    const beaconSuccess = navigator.sendBeacon(this.reportUrl, blob);
                    if (beaconSuccess) {
                        this.inFlightBatch = [];
                        this._scheduleStoragePersist(true); // 立即同步本地清空
                        this.isFlushing = false;
                        return;
                    }
                } catch (e) {
                    // 降级为常规 fetch
                }
            }

            // 3. 常规网络通道处理
            try {
                const response = await fetch(this.reportUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'text/plain; charset=utf-8'
                    },
                    body: payloadText,
                    keepalive: true
                });

                if (response.ok) {
                    // 发送成功，彻底丢弃待确认队列中的这批数据
                    this.inFlightBatch = [];
                    this._scheduleStoragePersist();
                } else {
                    // 服务端异常，重新合流到主队列头部
                    this.queue = [...this.inFlightBatch, ...this.queue];
                    this.inFlightBatch = [];
                }
            } catch (netErr) {
                // 网络异常或断网，重新放回队列
                this.queue = [...this.inFlightBatch, ...this.queue];
                this.inFlightBatch = [];
            } finally {
                this.isFlushing = false;
            }
        });
    }

    // =========================================================================
    // 本地持久化与冷启动防崩机制 (I/O 防抖优化)
    // =========================================================================

    /**
     * 异步防抖落盘，避免高频打点时疯狂产生 I/O 阻塞
     * @param {boolean} immediate 是否立即写入（不防抖）
     */
    _scheduleStoragePersist(immediate = false) {
        if (immediate) {
            if (this._storageSyncTimer) {
                clearTimeout(this._storageSyncTimer);
                this._storageSyncTimer = null;
            }
            this._doPersistStorage();
            return;
        }

        if (this._storageSyncTimer) return;

        // 延迟 500ms 批量聚合落盘一次
        this._storageSyncTimer = setTimeout(() => {
            this._storageSyncTimer = null;
            this._doPersistStorage();
        }, 500);
    }

    async _doPersistStorage() {
        try {
            // 合并内存队列与待确认队列形成全集底单
            const snapshot = [...this.inFlightBatch, ...this.queue];
            if (snapshot.length === 0) {
                await Storage.remove(STAT_PERSIST_KEY);
            } else {
                await Storage.set(STAT_PERSIST_KEY, snapshot);
            }
        } catch (e) {
            // 忽略持久化异常，保障运行
        }
    }

    /**
     * 静默捞取历史离线残留数据（冷启动恢复）
     */
    async _recoverOfflineQueue() {
        try {
            const historyData = await Storage.get(STAT_PERSIST_KEY);
            if (Array.isArray(historyData) && historyData.length > 0) {
                // 历史数据合并到队首
                this.queue = [...historyData, ...this.queue];
                // 触发一次冲刷排空历史
                this._triggerFlush(false);
            }
        } catch (e) {
            // ignore
        }
    }

    // =========================================================================
    // 监听原生宿主与生命周期抢跑
    // =========================================================================

    _bindLifecycleListeners() {
        // 1. Capacitor 原生 App 状态变更 (退到后台/切桌面)
        if (!isHarmony()) {
            try {
                CapacitorApp.addListener('appStateChange', ({ isActive }) => {
                    if (!isActive) {
                        this._triggerFlush(true); // 紧急清仓
                    }
                });
            } catch (e) {
                // ignore
            }
        }

        // 2. Web / 鸿蒙 WebView 挂起、销毁捕获
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this._triggerFlush(true);
                }
            });
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', () => {
                this._triggerFlush(true);
            });
            // 断网重连恢复，立即冲刷积压队列
            window.addEventListener('online', () => {
                this._triggerFlush(false);
            });
        }
    }
}

// 导出全局单例
export const statManager = new StatManager();

/**
 * 业务极简快捷打点方法 (纯同步，零阻塞)
 * @param {string} event 必填，事件名 (如: 'click_login')
 * @param {Object|string} [extra] 选填，业务参数 (如: { tab: 1 })
 */
export const trackStat = (event, extra) => {
    statManager.track(event, extra);
};

/**
 * Vue 插件安装器
 */
export const StatService = {
    install(app, options = {}) {
        statManager.init(options);

        // 注入到 Vue 全局属性，支持 Options API: this.$stat()
        app.config.globalProperties.$stat = (event, extra) => {
            statManager.track(event, extra);
        };

        // 注入 provide 支持组合式 inject('stat')
        app.provide('stat', statManager);
    }
};
